import { IUploadRecord } from '@/components/MassUpload/MassUpload.types';
import { IFileMetadata, IUploadUpdateDependenciesDto } from 'types';
import { IDocumentFromUpload } from 'types/lib/modules/upload-handler/upload-update-dependencies-dto.interface';
import {
  divideArray,
  getExtensionFromFilepath,
  getFileNameWithoutExtension,
  isDefined,
} from 'utils';
import { computed, ref, Ref, watch } from 'vue';

export function useUploadUpdateRecordsHandler(
  uploadRecords: Ref<IUploadRecord[]>,
  uploadUpdateDependencies: Ref<IUploadUpdateDependenciesDto | undefined>,
) {
  /** Records that were not part of the original upload */
  const invalidUploadRecords = ref<IUploadRecord[]>([]);

  watch(uploadRecords, handleUploadRecords, { once: true });

  function handleUploadRecords(): void {
    matchRenditionFiles();
    checkIfPresentInOriginalUpload();
    mapMetadataToUploadRecords();
  }

  function checkIfPresentInOriginalUpload(): void {
    const [valid, invalid] = divideArray(
      uploadRecords.value,
      (uploadRecord) =>
        !!uploadRecord.file?.name &&
        !!getOriginalByFileName(uploadRecord.file.name),
    );
    uploadRecords.value = valid;
    invalidUploadRecords.value = invalid;
  }

  const recordsWithMissingRenditionFiles = computed<string[]>(() => {
    return uploadRecords.value
      .map((record) => {
        const original = getOriginalByFileName(record.file!.name!);
        if (original?.renditionFile && !record.renditionFile) {
          return record.metadata?.documentId;
        }
      })
      .filter(isDefined);
  });

  function getOriginalByFileName(
    fileName: string,
  ): IDocumentFromUpload | undefined {
    return uploadUpdateDependencies.value?.documents.find(
      (doc) => doc.originalFileName === fileName,
    );
  }

  function matchRenditionFiles(): void {
    const grouped = uploadRecords.value.reduce(
      (acc: Record<string, IUploadRecord[]>, record) => {
        if (!record.file?.name) return acc;
        const fileName = getFileNameWithoutExtension(record.file.name);
        acc[fileName] ??= [];
        acc[fileName].push(record);
        return acc;
      },
      {},
    );

    uploadRecords.value = Object.entries(grouped).flatMap(
      ([fileName, records]) => {
        if (records.length === 1) return records;
        const pdfFile = records.find(
          (record) =>
            record.file?.name &&
            getFileNameWithoutExtension(record.file.name) === fileName &&
            getExtensionFromFilepath(record.file.name) === 'pdf',
        );
        const renditionFile = records.find(
          (record) =>
            record.file?.name &&
            getFileNameWithoutExtension(record.file.name) === fileName &&
            getExtensionFromFilepath(record.file.name) !== 'pdf',
        );
        if (pdfFile && renditionFile) {
          pdfFile.renditionFile = { file: renditionFile.file };
        }
        return pdfFile!;
      },
    );
  }

  function mapMetadataToUploadRecords(): void {
    uploadRecords.value.forEach((record) => {
      const docFromOriginalUpload =
        uploadUpdateDependencies.value?.documents.find(
          (doc) => doc.originalFileName === record.file?.name,
        );
      const metadata = docFromOriginalUpload!.documentMetadata as IFileMetadata;
      /**
       * Overwriting the Filepath field with the original file name because
       * files can only be updated directly (not in a ZIP or directory) when
       * performing an upload update.
       */
      record.metadata = { ...metadata, Filepath: record.file!.name };
      if (record.renditionFile) {
        record.renditionFile.metadata = {
          ...metadata,
          Filepath: record.renditionFile.file!.name,
        };
      }
    });
  }

  return { invalidUploadRecords, recordsWithMissingRenditionFiles };
}
